import type { ComponentPropsWithoutRef } from "react";

import { styled } from "@mui/material";
import type { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

export type NewOrnamentSize = "medium" | "small";
export type NewOrnamentPlacement = "inline" | "link";

export type NewOrnamentProps = Omit<ComponentPropsWithoutRef<"span">, "children"> & {
  size?: NewOrnamentSize;
  placement?: NewOrnamentPlacement;
  /** The ornament is removed at this timestamp. Omit it to render without an expiry. */
  until?: DateTime;
};

type NewOrnamentRootProps = {
  ornamentSize: NewOrnamentSize;
  placement: NewOrnamentPlacement;
};

const NewOrnamentRoot = styled("span", {
  shouldForwardProp: (prop) => prop !== "ornamentSize" && prop !== "placement",
})<NewOrnamentRootProps>(({ theme, ornamentSize, placement }) => ({
  alignItems: "center",
  backgroundColor: theme.palette.secondary.main,
  borderRadius: theme.shape.borderRadius,
  color: theme.palette.common.white,
  display: "inline-flex",
  flexShrink: 0,
  fontFamily: theme.typography.fontFamily,
  fontSize: ornamentSize === "small" ? 9 : 10,
  fontWeight: 500,
  height: ornamentSize === "small" ? 14 : 16,
  letterSpacing: "0.06em",
  lineHeight: 1,
  marginLeft: placement === "link" ? 4 : 8,
  padding: ornamentSize === "small" ? "0 4px" : "0 5px",
  textTransform: "none",
  transform: placement === "link" ? "translateY(-3px)" : undefined,
  whiteSpace: "nowrap",

  ".Mui-disabled &, button:disabled &": {
    backgroundColor: "rgba(0, 0, 0, 0.12)",
    color: "rgba(0, 0, 0, 0.38)",
  },
}));

export const NewOrnament = ({ size = "medium", placement = "inline", until, ...rest }: NewOrnamentProps) => {
  const { t } = useTranslation();
  const ornamentSize = placement === "link" ? "small" : size;

  if (until && (!until.isValid || Date.now() >= until.toMillis())) {
    return null;
  }

  return (
    <NewOrnamentRoot ornamentSize={ornamentSize} placement={placement} {...rest}>
      {t("common:new")}
    </NewOrnamentRoot>
  );
};
